import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DISCORD_AUTH_URL, GET_USER_URL, LOGOUT_URL, AUTH_STATUS_URL } from '../api';

// Create axios instance with consistent configuration for auth
axios.defaults.withCredentials = true;
const authAxios = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

// Log that this instance has been created with withCredentials
console.log('AuthAxios instance created with withCredentials=true');

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [redirectInProgress, setRedirectInProgress] = useState(false);
  
  // Check if user is authenticated
  const checkAuthStatus = useCallback(async () => {
    setLoading(true);
    try {
      // Don't make the API call if we're in the middle of a redirect
      if (redirectInProgress) {
        console.log('Auth redirect in progress, skipping server check');
        setLoading(false);
        return;
      }

      console.log('Checking auth status with server...');
      const response = await authAxios.get(AUTH_STATUS_URL);
      
      console.log('Auth status response:', response.data);
      
      const { authenticated, user: userData } = response.data;
      
      if (authenticated && userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log('Not authenticated according to server');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, [redirectInProgress]);
  
  // Login with Discord OAuth
  const loginWithDiscord = useCallback(() => {
    console.log('Starting Discord login process');
    setRedirectInProgress(true);
    
    // Add timestamp to prevent caching issues
    const timestamp = Date.now();
    window.location.href = `${DISCORD_AUTH_URL}?t=${timestamp}`;
  }, []);
  
  // Check if we're returning from Discord auth
  useEffect(() => {
    const checkUrlParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hasAuthFlag = urlParams.has('auth');
      
      if (hasAuthFlag) {
        console.log('Auth redirect detected, will check server status');
        // Remove params from URL to prevent issues on refresh
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Wait longer for cookies to settle before checking auth status
        setTimeout(() => {
          setRedirectInProgress(false);
          // Implement retry logic for auth checks
          let attempts = 0;
          const maxAttempts = 3;
          
          const checkWithRetry = async () => {
            try {
              console.log(`Auth check attempt ${attempts + 1} of ${maxAttempts}`);
              const response = await authAxios.get(AUTH_STATUS_URL);
              
              console.log('Auth status response:', response.data);
              
              const { authenticated, user: userData } = response.data;
              
              if (authenticated && userData) {
                setUser(userData);
                setIsAuthenticated(true);
              } else {
                console.log('Not authenticated according to server, retrying...');
                attempts++;
                if (attempts < maxAttempts) {
                  // Wait 2 seconds between retries
                  setTimeout(checkWithRetry, 2000);
                } else {
                  console.log('Max auth check attempts reached, giving up');
                  setUser(null);
                  setIsAuthenticated(false);
                }
              }
            } catch (error) {
              console.error('Auth check error:', error);
              attempts++;
              if (attempts < maxAttempts) {
                // Wait 2 seconds between retries
                setTimeout(checkWithRetry, 2000);
              } else {
                setUser(null);
                setIsAuthenticated(false);
              }
            }
          };
          
          checkWithRetry();
        }, 5000);
      } else {
        setRedirectInProgress(false);
      }
    };
    
    checkUrlParams();
  }, [checkAuthStatus]);
  
  // Run auth check on component mount
  useEffect(() => {
    if (!redirectInProgress) {
      checkAuthStatus();
    }
  }, [checkAuthStatus, redirectInProgress]);
  
  // Logout
  const logout = async () => {
    try {
      await authAxios.get(LOGOUT_URL);
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      // Force logout on client side
      setUser(null);
      setIsAuthenticated(false);
    }
  };
  
  const value = {
    user,
    isAuthenticated,
    loading,
    loginWithDiscord,
    logout
  };
  
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 